/**
 * arbi notifications list — list non-DM notifications (workspaceuser_invited,
 *                           contact_accepted, …)
 *
 * `arbi dm list` is intentionally narrowed to `user_message` rows. This view
 * is the firehose for everything else the server pushes — invites, contact
 * accepts, batch completes, etc. — and is the right place for agents that
 * need to react to system-level events without a long-running `arbi watch`
 * subscription.
 *
 * Backed by the same GET /v1/notifications/ endpoint as `dm list`; we just
 * filter to non-DM types.
 */

import { Command } from 'commander'
import { dm as dmOps } from '@arbidocs/sdk'
import {
  runAction,
  resolveDmCrypto,
  printTable,
  printJson,
  truncate,
  suggestSubcommandAndExit,
  dryRun,
} from '../helpers.js'

type Row = Record<string, unknown>

export function registerNotificationsCommand(program: Command): void {
  // `enablePositionalOptions` keeps `--json` (declared on both parent and
  // `list`) attached to whichever command the user actually wrote it after.
  // Without this commander resolves the parent option first and the inner
  // list subcommand's --json never fires.
  const notif = program
    .command('notifications')
    .enablePositionalOptions()
    .description('Non-DM notifications')

  notif
    .command('list')
    .description('List non-DM notifications (workspace invites, contact accepts, …)')
    .option('--unread', 'Only show unread notifications')
    .option('--type <comma-list>', 'Filter to specific type(s), e.g. workspaceuser_invited')
    .option('-l, --limit <n>', 'Cap to N most-recent', (v) => parseInt(v, 10))
    .option('--json', 'Output as JSON (recommended for scripting / agents)')
    .action((opts: { unread?: boolean; type?: string; limit?: number; json?: boolean }) =>
      runAction(async () => {
        const { arbi, crypto } = await resolveDmCrypto()
        // `listDecryptedDMs` returns ALL notifications and only DM rows have
        // decryptable content — non-DM rows pass through with `content`
        // already set to whatever the server emitted (usually null/empty).
        const all = (await dmOps.listDecryptedDMs(arbi, crypto)) as Row[]
        const nonDm = all.filter((r) => (r.type as string) !== 'user_message')

        const typeFilter = opts.type
          ? new Set(
              opts.type
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean)
            )
          : null

        const filtered = nonDm.filter((r) => {
          if (opts.unread && r.read) return false
          if (typeFilter && !typeFilter.has((r.type as string) ?? '')) return false
          return true
        })

        const limited = opts.limit && opts.limit > 0 ? filtered.slice(0, opts.limit) : filtered

        if (opts.json) {
          printJson(limited)
          return
        }

        if (limited.length === 0) {
          process.stderr.write('No notifications.\n')
          return
        }

        printTable(
          [
            { header: 'ID', width: 16, value: (r) => r.external_id as string },
            { header: 'TYPE', width: 24, value: (r) => (r.type as string) ?? '' },
            { header: 'READ', width: 6, value: (r) => (r.read ? 'yes' : 'no') },
            {
              header: 'CONTENT',
              width: 50,
              // Non-DM rows usually carry a JSON-encoded payload in
              // `content`. Show the first slice and let the JSON path
              // expose the full thing for agents that want to introspect.
              value: (r) => truncate((r.content as string) ?? '', 49),
            },
          ],
          limited
        )
      })()
    )

  // ── mark-read ────────────────────────────────────────────────────────────
  // Closes the long-running-agent feedback loop: without --all, the
  // `notifications --unread` filter grows unbounded after a burst of
  // workspace invites or task completions.
  notif
    .command('mark-read [ids...]')
    .description('Mark notifications as read (use --all to drain the unread queue)')
    .option('--all', 'Mark every unread non-DM notification as read')
    .action((ids: string[] | undefined, opts: { all?: boolean }) =>
      runAction(async () => {
        const { arbi, crypto } = await resolveDmCrypto()
        let toMark = ids && ids.length > 0 ? ids : undefined
        if (!toMark && opts.all) {
          const all = (await dmOps.listDecryptedDMs(arbi, crypto)) as Row[]
          toMark = all
            .filter((r) => (r.type as string) !== 'user_message' && !r.read)
            .map((r) => r.external_id as string)
        }
        if (!toMark || toMark.length === 0) {
          process.stderr.write('Nothing to mark read. (Pass IDs or --all.)\n')
          return
        }
        const data = await dmOps.markRead(arbi, toMark)
        process.stdout.write(`Marked ${data.length} notification(s) as read.\n`)
      })()
    )

  // ── delete ───────────────────────────────────────────────────────────────
  notif
    .command('delete [ids...]')
    .description('Delete notifications by ID (or use --all to clear non-DM noise)')
    .option('--all', 'Delete every non-DM notification (workspace invites, contact accepts, ...)')
    .option('--dry-run', 'Preview which notifications would be deleted (no SDK call)')
    .action((ids: string[] | undefined, opts: { all?: boolean; dryRun?: boolean }) =>
      runAction(async () => {
        const { arbi, crypto } = await resolveDmCrypto()
        let toDelete = ids && ids.length > 0 ? ids : undefined
        if (!toDelete && opts.all) {
          const all = (await dmOps.listDecryptedDMs(arbi, crypto)) as Row[]
          toDelete = all
            .filter((r) => (r.type as string) !== 'user_message')
            .map((r) => r.external_id as string)
        }
        if (!toDelete || toDelete.length === 0) {
          process.stderr.write('Nothing to delete. (Pass IDs or --all.)\n')
          return
        }
        if (opts?.dryRun) {
          dryRun(`delete ${toDelete.length} notification(s)`, toDelete)
          return
        }
        await dmOps.deleteDMs(arbi, toDelete)
        process.stdout.write(`Deleted ${toDelete.length} notification(s).\n`)
      })()
    )

  // Default action: forward unknown args (including --json/--unread/etc.) to
  // the `list` subcommand. We avoid pre-declaring those options on the parent
  // because commander 11 binds them to whichever command declared them first
  // — `arbi notifications list --json` then dispatches `list` with an empty
  // opts and the data never gets JSON-formatted.
  notif
    .allowUnknownOption(true)
    .allowExcessArguments(true)
    .action(async () => {
      const tail = notif.args ?? []
      const subcommands = notif.commands.map((c) => c.name())
      const firstNonFlag = tail.find((a) => !a.startsWith('-'))
      if (firstNonFlag && !subcommands.includes(firstNonFlag)) {
        suggestSubcommandAndExit('notifications', firstNonFlag, subcommands)
      }
      await notif.commands.find((c) => c.name() === 'list')!.parseAsync(tail, { from: 'user' })
    })
}
